import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

const DiagnosticZone = () => {
  const { user } = useAuth();
  const [apiStatus, setApiStatus] = useState(null);
  const [authTest, setAuthTest] = useState(null);
  const [zoneDataTest, setZoneDataTest] = useState(null);

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    // Test de connexion API
    try {
      console.log('Test de connexion API...');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/test-connection`);
      const data = await response.json();
      setApiStatus({ success: true, data });
      console.log('✅ API connectée');
    } catch (error) {
      setApiStatus({ success: false, error: error.message });
      console.error('❌ Erreur API:', error);
    }

    // Test d'authentification
    try {
      console.log('Test d\'authentification...');
      const response = await apiService.get('/auth/me');
      setAuthTest({ success: true, user: response.user });
      console.log('✅ Authentification OK');
    } catch (error) {
      setAuthTest({ success: false, error: error.response?.data || error.message });
      console.error('❌ Erreur auth:', error);
    }

    // Test des données de zone
    try {
      console.log('Test données de zone...');
      const response = await apiService.get('/zone-dashboard/');
      setZoneDataTest({ success: true, data: response });
      console.log('✅ Données de zone OK');
    } catch (error) {
      setZoneDataTest({ success: false, error: error.response?.data || error.message });
      console.error('❌ Erreur zone:', error);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">🔍 Diagnostic Zone Dashboard</h1>
      
      <div className="space-y-6">
        {/* Informations utilisateur */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">👤 Utilisateur Connecté</h2>
          {user ? (
            <div className="space-y-2">
              <p><strong>Nom:</strong> {user.first_name} {user.last_name}</p>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Rôle:</strong> {user.role}</p>
              <p><strong>Zone:</strong> {user.zone || 'Aucune zone assignée'}</p>
              <p><strong>Fraternité:</strong> {user.fraternity || 'Aucune fraternité'}</p>
            </div>
          ) : (
            <p className="text-red-600">Aucun utilisateur connecté</p>
          )}
        </div>

        {/* Test API */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">🔗 État de l'API Backend</h2>
          {apiStatus === null ? (
            <p>Test en cours...</p>
          ) : apiStatus.success ? (
            <div className="text-green-600">
              <p>✅ Backend Laravel accessible</p>
              <p><strong>Version:</strong> {apiStatus.data.version}</p>
              <p><strong>Timestamp:</strong> {apiStatus.data.timestamp}</p>
            </div>
          ) : (
            <div className="text-red-600">
              <p>❌ Erreur de connexion au backend</p>
              <p><strong>Erreur:</strong> {apiStatus.error}</p>
              <p className="text-sm mt-2">Vérifiez que le serveur API est accessible sur {process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000'}</p>
            </div>
          )}
        </div>

        {/* Test Auth */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">🔐 Test d'Authentification</h2>
          {authTest === null ? (
            <p>Test en cours...</p>
          ) : authTest.success ? (
            <div className="text-green-600">
              <p>✅ Authentification valide</p>
              <p><strong>Token:</strong> Valide</p>
            </div>
          ) : (
            <div className="text-red-600">
              <p>❌ Problème d'authentification</p>
              <p><strong>Erreur:</strong> {typeof authTest.error === 'string' ? authTest.error : JSON.stringify(authTest.error)}</p>
            </div>
          )}
        </div>

        {/* Test Zone Data */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">🌍 Test Données de Zone</h2>
          {zoneDataTest === null ? (
            <p>Test en cours...</p>
          ) : zoneDataTest.success ? (
            <div className="text-green-600">
              <p>✅ Accès aux données de zone</p>
              <p><strong>Zone:</strong> {zoneDataTest.data.zone}</p>
              <p><strong>Fraternités:</strong> {zoneDataTest.data.fraternities?.length || 0}</p>
            </div>
          ) : (
            <div className="text-red-600">
              <p>❌ Erreur d'accès aux données de zone</p>
              <p><strong>Erreur:</strong> {typeof zoneDataTest.error === 'string' ? zoneDataTest.error : JSON.stringify(zoneDataTest.error)}</p>
              
              {zoneDataTest.error?.message && (
                <div className="mt-3 p-3 bg-red-50 rounded">
                  <p><strong>Message:</strong> {zoneDataTest.error.message}</p>
                  {zoneDataTest.error.message.includes('zone assignée') && (
                    <p className="text-sm mt-1">
                      💡 <strong>Solution:</strong> L'utilisateur doit avoir une zone assignée pour accéder au dashboard de zone.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">🔧 Actions</h2>
          <div className="space-y-3">
            <button
              onClick={runDiagnostics}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              🔄 Relancer les tests
            </button>
            
            <div className="text-sm text-gray-600">
              <h3 className="font-medium mb-2">Instructions de dépannage :</h3>
              <ol className="list-decimal list-inside space-y-1">
                <li>Vérifiez que le serveur Laravel est démarré : <code>php artisan serve</code></li>
                <li>Vérifiez que vous êtes connecté avec un compte ayant une zone assignée</li>
                <li>Utilisez un des comptes de test : test.membre.cotonou@fsf.com</li>
                <li>Vérifiez la console du navigateur pour plus de détails</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticZone;